import React, { useState, useEffect } from 'react';
import {
  CharacterType,
  CharacterTypeEnhancement,
  listCharacterTypes,
  getCharacterTypeEnhancements,
  generateTestAvatar,
  updateCharacterTypeEnhancement,
} from '../../api/admin';
import './Admin.css';

interface HistoryEntry {
  avatarUrl: string;
  level: number; // 0 for base, 2-4 for enhancements
  optionId?: string;
  optionName?: string;
  prompt?: string;
}

const EnhancementTester: React.FC = () => {
  const [characterTypes, setCharacterTypes] = useState<CharacterType[]>([]);
  const [selectedType, setSelectedType] = useState<CharacterType | null>(null);
  const [enhancements, setEnhancements] = useState<CharacterTypeEnhancement[]>([]);
  const [currentLevel, setCurrentLevel] = useState<number>(2);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [editPrompt, setEditPrompt] = useState('');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listCharacterTypes()
      .then(setCharacterTypes)
      .catch(() => setError('Failed to load character types'))
      .finally(() => setLoading(false));
  }, []);

  const handleSelectType = async (type: CharacterType) => {
    setSelectedType(type);
    setError(null);
    setSaveSuccess(null);
    try {
      const opts = await getCharacterTypeEnhancements(type.id);
      setEnhancements(opts);
    } catch {
      setEnhancements([]);
      setError('Failed to load enhancement options');
    }
    const baseEntry: HistoryEntry = {
      avatarUrl: type.avatar_url,
      level: 0,
    };
    setHistory([baseEntry]);
    setHistoryIndex(0);
    setCurrentLevel(2);
    setSelectedOptionId(null);
    setEditPrompt('');
  };

  const handleReset = () => {
    setSelectedType(null);
    setEnhancements([]);
    setHistory([]);
    setHistoryIndex(0);
    setCurrentLevel(2);
    setSelectedOptionId(null);
    setEditPrompt('');
    setError(null);
    setSaveSuccess(null);
  };

  const currentAvatar = history.length > 0 ? history[historyIndex].avatarUrl : '';

  const optionsForLevel = enhancements.filter(e => e.level === currentLevel);

  const selectedOption = optionsForLevel.find(o => o.id === selectedOptionId);

  const getTypeName = () => {
    if (!selectedOption) return 'item';
    return selectedOption.type_name || 'item';
  };

  const getFullPrompt = () => {
    if (!editPrompt.trim()) return '';
    const typeName = getTypeName();
    return `Remove and replace any existing ${typeName} the minifigure may be wearing, and make them ${editPrompt}. Keep the same character style, pose, proportions, and background.`;
  };

  const handleOptionSelect = (optionId: string) => {
    setSelectedOptionId(optionId);
    const opt = optionsForLevel.find(o => o.id === optionId);
    if (opt) {
      setEditPrompt(opt.add_prompt);
    }
    setSaveSuccess(null);
  };

  const handleGenerate = async () => {
    if (!editPrompt.trim() || !currentAvatar) return;
    setGenerating(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const result = await generateTestAvatar({
        base_image_url: currentAvatar,
        prompt: editPrompt,
        option_id: selectedOptionId || undefined,
      });

      const newEntry: HistoryEntry = {
        avatarUrl: result.avatar_url,
        level: currentLevel,
        optionId: selectedOptionId || undefined,
        optionName: selectedOption?.name,
        prompt: editPrompt,
      };

      // Branch: remove entries after current index, add new one
      const newHistory = [...history.slice(0, historyIndex + 1), newEntry];
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);

      // Advance to next level if available
      if (currentLevel < 4) {
        setCurrentLevel(currentLevel + 1);
        setSelectedOptionId(null);
        setEditPrompt('');
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSavePrompt = async () => {
    if (!selectedOptionId || !editPrompt.trim()) return;
    setSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      await updateCharacterTypeEnhancement(selectedOptionId, {
        add_prompt: editPrompt,
      });
      // Update local state
      setEnhancements(prev =>
        prev.map(e => e.id === selectedOptionId ? { ...e, add_prompt: editPrompt } : e)
      );
      setSaveSuccess(`Saved prompt to "${selectedOption?.name}"`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleHistoryClick = (index: number) => {
    setHistoryIndex(index);
    const entry = history[index];
    // Set current level to the next level after this entry
    if (entry.level === 0) {
      setCurrentLevel(2);
    } else if (entry.level < 4) {
      setCurrentLevel(entry.level + 1);
    } else {
      setCurrentLevel(4);
    }
    setSelectedOptionId(null);
    setEditPrompt('');
    setSaveSuccess(null);
  };

  if (loading) {
    return <div style={{ padding: 20, color: 'var(--text-secondary)' }}>Loading...</div>;
  }

  // Character type selection
  if (!selectedType) {
    return (
      <div>
        <div className="admin-page-header">
          <h2>Enhancement Tester</h2>
        </div>
        <div className="admin-alert admin-alert-info">
          Each generation uses the NanoBanana API (Gemini). Generations are not free — use thoughtfully.
        </div>
        <h3 style={{ marginBottom: 15, color: 'var(--text-color)' }}>Select a Character Type</h3>
        <div className="tester-avatar-grid">
          {characterTypes.map(type => (
            <div
              key={type.id}
              className="tester-avatar-option"
              onClick={() => handleSelectType(type)}
            >
              <img src={type.avatar_url} alt={type.name} />
              <span>{type.name}</span>
            </div>
          ))}
        </div>
        {characterTypes.length === 0 && (
          <div className="admin-empty">
            <p>No character types found.</p>
          </div>
        )}
      </div>
    );
  }

  // Testing mode
  const allLevelsComplete = currentLevel >= 4 && history[historyIndex]?.level === 4;

  return (
    <div>
      <div className="admin-page-header">
        <h2>Enhancement Tester</h2>
        <button className="admin-btn admin-btn-secondary" onClick={handleReset}>
          Reset
        </button>
      </div>

      <div className="admin-alert admin-alert-info">
        Each generation uses the NanoBanana API (Gemini). Generations are not free — use thoughtfully.
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {saveSuccess && <div className="admin-alert admin-alert-success">{saveSuccess}</div>}

      <div className="tester-layout">
        {/* Left panel: Avatar + History */}
        <div className="tester-avatar-section">
          <div className="tester-avatar-display">
            {generating ? (
              <div className="tester-avatar-loading">
                <div className="tester-spinner" />
                <span>Generating...</span>
              </div>
            ) : currentAvatar ? (
              <img src={currentAvatar} alt="Current avatar" />
            ) : null}
          </div>

          {history.length > 0 && (
            <div className="tester-history">
              <h4>History</h4>
              {history.map((entry, i) => (
                <div
                  key={i}
                  className={`tester-history-item ${i === historyIndex ? 'active' : ''}`}
                  onClick={() => handleHistoryClick(i)}
                >
                  {entry.level === 0
                    ? `Base: ${selectedType.name}`
                    : `Lv${entry.level}: + ${entry.optionName || 'Custom'}`}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right panel: Controls */}
        <div className="tester-controls">
          <div className="admin-card">
            <h3>
              Level {currentLevel} Enhancements
              {allLevelsComplete && ' (Complete)'}
            </h3>

            {optionsForLevel.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', marginBottom: 15 }}>
                No enhancement options for {selectedType.name} at level {currentLevel}.
              </p>
            ) : (
              <div style={{ marginBottom: 15 }}>
                {optionsForLevel.map(opt => (
                  <label
                    key={opt.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 0',
                      cursor: 'pointer',
                      color: 'var(--text-color)',
                    }}
                  >
                    <input
                      type="radio"
                      name="enhancement-option"
                      checked={selectedOptionId === opt.id}
                      onChange={() => handleOptionSelect(opt.id)}
                      disabled={generating}
                    />
                    <span>
                      <strong>{opt.name}</strong>
                      <span style={{ color: 'var(--text-secondary)', marginLeft: 6, fontSize: '0.85rem' }}>
                        ({opt.type_name})
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}

            <div className="admin-form-group">
              <label>Add Prompt</label>
              <textarea
                className="admin-form-input tester-prompt-area"
                value={editPrompt}
                onChange={e => { setEditPrompt(e.target.value); setSaveSuccess(null); }}
                placeholder="Enter or edit the enhancement prompt..."
                disabled={generating}
              />
            </div>

            {editPrompt.trim() && (
              <div className="tester-prompt-preview">
                <strong style={{ fontSize: '0.8rem', display: 'block', marginBottom: 4 }}>
                  Full prompt preview:
                </strong>
                Remove and replace any existing {getTypeName()} the minifigure may be wearing, and make them{' '}
                <em>{editPrompt}</em>. Keep the same character style, pose, proportions, and background.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 15 }}>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleGenerate}
                disabled={!editPrompt.trim() || generating}
              >
                {generating ? 'Generating...' : 'Generate'}
              </button>
              {selectedOptionId && (
                <button
                  className="admin-btn admin-btn-secondary"
                  onClick={handleSavePrompt}
                  disabled={!editPrompt.trim() || saving || generating}
                >
                  {saving ? 'Saving...' : 'Save Prompt'}
                </button>
              )}
            </div>
          </div>

          {/* Level navigation */}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {[2, 3, 4].map(level => (
              <button
                key={level}
                className={`admin-btn ${currentLevel === level ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
                style={{ fontSize: '0.8rem' }}
                onClick={() => {
                  setCurrentLevel(level);
                  setSelectedOptionId(null);
                  setEditPrompt('');
                  setSaveSuccess(null);
                }}
              >
                Level {level}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancementTester;
